import React, { useMemo, useState } from 'react';
import { useSetWidgetAttribute } from '../../hooks/useSetWidgetAttribute';
import { useSelector, useDispatch } from 'react-redux';
import { selectWidgetEffectiveCols } from '../../redux/datasourceSlice.js';
import CollapsibleSection from '../../../common/CollapsibleSection.jsx';
import { MapConstants } from './mapConstants';
import MapWidgetContentSettingsRow from './MapWidgetContentSettingsRow.jsx';
import Select from '../../../common/Select.jsx';
import { cloneDeep } from 'lodash';
import ColorPicker from '../../../common/ColorPicker/DashboardColorPicker.jsx';
import styles from '../base/CommonSettings.module.scss';
import stylesGroupItem from '../../settings_sidebar/common/ListGroupItem.module.scss';
import Portal from '../../../common/Portal.jsx';
import { closeModal, openModal, saveWidgetSettings } from '../../redux/dashboardSlice.js';
import { FaIcon } from '../../../common/Icons.jsx';
import Dialog, { DialogFooter, DialogHeader } from '../../../bootstrap/Dialog.jsx';
import WidgetTitleSettings from '../../settings_sidebar/WidgetTitleSettings.jsx';

function SettingsDialog({InnerComponent, ...props}) {
    return (
        <Dialog {...props}>
            {InnerComponent}
        </Dialog>
    )
}

function MapWidgetContentSettings({ widgetId }){
  const dispatch = useDispatch();
  const setAttribute = useSetWidgetAttribute(widgetId);
  const getFieldFilterKey = f => `field_is_filter:${f}`;
  const getFieldOutputKey = f => `field_selected_output:${f}`;
  const getFieldFilterLabel = f => I18n.t(`mwf_${f}`);
  const getSettingValue = key => settings[key];

  const loading = useSelector(state => state.dashboard.loading);
  const settings = useSelector(state => state.dashboard.widgets[widgetId].settings || {});
  const additionalFields = useSelector(state => state.dashboard.widgets[widgetId].settings?.additionalFields) || [];
  const headerNames = useSelector(selectWidgetEffectiveCols(widgetId));
  const headerNameOpts = headerNames.map((c) => ({
                          value: c.accessor,
                          label: c.name,
                        }));

  const settingDatasourceOptions = useMemo(() => {
    return [{value: "", label: I18n.t("select_from_list")}, ...headerNameOpts]
  }, [headerNameOpts]);

  const initialFilter = useMemo(() => {
    let filterArr = []
    MapConstants.FILTERABLE_DATA_FIELDS.forEach((f) => {
      const key = getFieldFilterKey(f)
      if (settings[key]){
        filterArr.push(key)
      }
    })
    additionalFields.forEach((f , i) => {
      if (f.filtering){
        filterArr.push(`additionalFields.${i}`)
      }
    })
    return filterArr
  }, [])
  const [filters, setFilters] = useState(initialFilter);

  const [dataValIndex, setDataValIndex] = useState(null);


  const addAdditionalField = () => {
    let currentFields = [...additionalFields];
    currentFields.push({
      id: StringHelper.random(8),
      field_label: "",
      field_value: ""
    });

    setAttribute('settings.additionalFields', currentFields);
  };

  const updateAdditionalField = (key, index, value) => {
      const currentFields = cloneDeep(additionalFields);
      let field = {...currentFields[index]};
      field[key] = value
      currentFields[index] = field;
      setAttribute('settings.additionalFields', currentFields);
  };

  const removeAdditionalField = (index) => {
    let currentFields = [...additionalFields];
    currentFields.splice(index, 1)

    setAttribute('settings.additionalFields', currentFields);
  };

  const filterOpts = useMemo(() => {
    const dataFilters = MapConstants.FILTERABLE_DATA_FIELDS.map(f => ({value: getFieldFilterKey(f), label: getFieldFilterLabel(f)}))
    const additionalFieldsFilter = additionalFields.map((f, i) => ({value: `additionalFields.${i}`, label: f?.field_label || I18n.t('unnamed')}))
    return dataFilters.concat(additionalFieldsFilter)
  }
  , [additionalFields])

  const onChangeFilter = (values) => {
    const diff = [
      ...filters.filter(o => !values.some(n => n === o)),
      ...values.filter(n => !filters.some(o => o === n))
    ];

    // Toggle the element that change
    diff.forEach(v => {
      if (v.includes('additionalFields')){
        const index = v.split('.').pop();
        const filtering = additionalFields[index].filtering;
        updateAdditionalField('filtering', parseInt(index), !filtering)
      } else {
        setAttribute(`settings.${v}`, !getSettingValue(v))
      }
    })
    setFilters(values);
  }

  const toggleModal = (index) => {
      if (index != null) {
          dispatch(openModal());
      } else {
          dispatch(closeModal());
      }
      setDataValIndex(index);
  }

  const saveSettings = async () => {
      await dispatch(saveWidgetSettings());
      toggleModal(null);
  }

  const divisionOpts = MapConstants.DIVISION_OPTIONS.map(o => ({value: o, label: o}))

  const regionSelectOpts = useMemo(() => {
    const regionOpts = MapConstants.REGION_OPTIONS.map(o => ({value: o, label: o}))
    const countryOpts = Object.keys(MapConstants.COUNTRY_CODES).map(k => ({value: k, label: MapConstants.COUNTRY_CODES[k]}))
    return regionOpts.concat(countryOpts, divisionOpts)
  }, [])
  return (
    <CollapsibleSection title={I18n.t('content_settings')}>
        <WidgetTitleSettings widgetId={widgetId} />
        <div id={`filter-container-${widgetId}`} style={{display: 'none'}} />
        <div className="table-content widget-container" id={`map-container-${widgetId}`} style={{display: 'none'}} />
        <div>
          <fieldset>
            <label style={{ width: '100%' }}>
              {I18n.t('region')}
              <Select
                options={regionSelectOpts}
                value={settings.region}
                onChange={(v) => setAttribute('settings.region', v)}
              />
            </label>
            <label style={{ width: '100%' }}>
              {I18n.t('division')}
              <Select
                options={divisionOpts}
                value={settings.division}
                onChange={(v) => setAttribute('settings.division', v)}
              />
            </label>
            <label className="w-100">
              {I18n.t("number_format")}
              <Select
                options={MapConstants.NUMBER_FORMAT_OPTIONS.map(o => ({value: o, label: o}))}
                value={settings.number_format}
                onChange={(v) => setAttribute('settings.number_format', v)}
              />
            </label>
            <label className="w-100">
              {I18n.t("map_projection")}
              <Select
                options={MapConstants.MAP_PROJECTION_OPTIONS.map(o => ({value: o, label: I18n.t(o)}))}
                value={settings.map_projection}
                onChange={(v) => setAttribute('settings.map_projection', v)}
              />
            </label>
            {Object.entries(MapConstants.ADDITIONAL_SETTINGS).map(([k, v]) =>
            <CollapsibleSection title={I18n.t(k)} key={k} initialCollapsed={true}>
                {
                  v.map((o, i) => {
                    return (
                      o ?
                      <label key={o} className={styles['settings-option']}>
                        <div>{I18n.t(`${o}_title`)}</div>
                        <ColorPicker value={settings[o]} onChange={(value) => setAttribute(`settings.${o}`, value)} />
                      </label> : <br key={i}/>
                    )
                  })
                }
              </CollapsibleSection>)}
          </fieldset>
          <CollapsibleSection title={I18n.t('data')}>
            {
              MapConstants.DATA_FIELDS.map(f =>{
                const key = getFieldOutputKey(f);
                const value = getSettingValue(key);
                const label = getFieldFilterLabel(f);
                return (
                  <label key={key} className={styles['settings-option']}>
                    {label}
                    <Select
                      options={settingDatasourceOptions}
                      onChange={(v) => setAttribute(`settings.${key}`, v)}
                      value={value}
                    />
                  </label>
              )})
            }
            <label>{I18n.t('data_values')}</label>
            <div className='list-group'>
              {additionalFields.map((v, i) => (
                <li key={`additional-field-${i}`} className={`widget-container list-group-item ${stylesGroupItem['list-group-item']}`}>
                    <div className={stylesGroupItem['list-group-item-content']}>
                        <div className={stylesGroupItem['list-group-item-description']}>
                            <h4 className='text-truncate'>{I18n.t('data')}</h4>
                            <p>{v?.field_label || I18n.t('unnamed')}</p>
                        </div>
                    </div>
                    <div className={stylesGroupItem['list-group-item-actions']} style={{justifyContent: 'flex-end'}}>
                        <a onClick={() => toggleModal(i)}>
                            <FaIcon type='cog' style={{color: 'black'}} />
                        </a>
                        <a onClick={(e) => removeAdditionalField(i)} className='ml-1'>
                            <FaIcon type='trash' className='text-danger' />
                        </a>
                    </div>
                </li>
              ))
              }
          </div>
            <button className="btn btn-primary" onClick={addAdditionalField}>
              {I18n.t("add")}
            </button>  
          </CollapsibleSection>
          <CollapsibleSection title={I18n.t('filter')}>
            <Select
              options={filterOpts}
              value={filters}
              onChange={onChangeFilter}
              isMulti={true}
            />
          </CollapsibleSection>
        </div>
        {dataValIndex != null && <Portal
            component={SettingsDialog}
            /* SettingsDialog props */
            header={<DialogHeader showBorder={true} title={I18n.t('edit')} onClose={() => toggleModal()} />}
            footer={<DialogFooter onAdd={saveSettings} onClose={() => toggleModal()} addText={loading ? I18n.t('loading') : I18n.t('save')} />}
            disabled={loading}
            size='md'
            closeOnOutsideClick={true}
            onClose={() => toggleModal()}
            InnerComponent={
                <MapWidgetContentSettingsRow
                        {...({index: dataValIndex, settingDatasourceOptions, updateAdditionalField, additionalFields})}
                />
            }
        />}
    </CollapsibleSection>
  )
  
}

export default MapWidgetContentSettings;